import { api } from './index';

import type { Milestone, MilestoneSteps } from '@/types';

export interface GetMilestoneParams {
  id: string;
}

export const milestoneApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createMilestone: builder.mutation<Milestone, Partial<Milestone>>({
      invalidatesTags: ['Milestone'],
      query: (milestone) => ({
        data: milestone,
        method: 'POST',
        url: '/milestones',
      }),
    }),
    deleteMilestone: builder.mutation<void, string>({
      invalidatesTags: (_result, _error, id) => [
        { id, type: 'Milestone' },
        'Milestone',
      ],
      query: (id) => ({
        method: 'DELETE',
        url: `/milestones/${id}`,
      }),
    }),
    getMilestone: builder.query<Milestone, GetMilestoneParams>({
      providesTags: (_result, _error, { id }) => [{ id, type: 'Milestone' }],
      query: ({ id }) => ({ method: 'GET', url: `/milestones/${id}` }),
    }),
    getMilestones: builder.query<MilestoneSteps, void>({
      providesTags: ['Milestone'],
      query: () => ({ method: 'GET', url: '/milestones' }),
    }),
    getMilestoneSuggestedSteps: builder.query<string[], string>({
      providesTags: (_result, _error, milestoneId) => [{ id: milestoneId, type: 'Milestone' }],
      query: (milestoneId) => ({ 
        method: 'GET', 
        url: `/milestones/${milestoneId}/suggestions` 
      }),
    }),
    refreshMilestoneSuggestedStep: builder.mutation<
      string[], 
      { index: number; milestoneId: string }
    >({
      invalidatesTags: (_result, _error, { milestoneId }) => [
        { id: milestoneId, type: 'Milestone' }
      ],
      query: ({ index, milestoneId }) => ({
        data: { milestone: { index } },
        method: 'PATCH',
        url: `/milestones/${milestoneId}/refresh_step`,
      }),
    }),
    updateMilestone: builder.mutation<
      Milestone, 
      Partial<Milestone> & { isComplete?: boolean }
    >({
      invalidatesTags: (_result, _error, { id }) => [
        { id, type: 'Milestone' },
        'Milestone',
      ],
      query: ({ id, ...milestone }) => ({
        data: { milestone },
        method: 'PATCH',
        url: `/milestones/${id}`,
      }),
    }),
  }),
});

export const {
  useCreateMilestoneMutation,
  useDeleteMilestoneMutation,
  useGetMilestoneQuery,
  useGetMilestonesQuery,
  useGetMilestoneSuggestedStepsQuery,
  useRefreshMilestoneSuggestedStepMutation,
  useUpdateMilestoneMutation,
} = milestoneApi;
